import { type FC, useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import type { RecapResponse } from '@eluve/api-contract';
import { Grid, Icon, Stack, Text } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

const compactDate = (value: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(2, 4)}`;
};

export const PreVisitRecap: FC<{
  tenantId: string;
  appointmentId: string;
  patientId: string | null;
}> = ({ tenantId, appointmentId, patientId }) => {
  const { t } = useTranslation();
  const tsRestClient = useApiClient();
  const [recap, setRecap] = useState<RecapResponse | null>(null);

  useEffect(() => {
    setRecap(null);
    if (!patientId) return;

    let alive = true;
    void tsRestClient.carePlans
      .getRecap({ params: { tenantId }, query: { appointmentId } })
      .then((r) => {
        if (alive && r.status === 200) setRecap(r.body);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [tsRestClient, tenantId, appointmentId, patientId]);

  if (
    !recap ||
    (recap.goals.length === 0 &&
      recap.lastVisitActionItems.length === 0 &&
      recap.measuresDue.length === 0)
  ) {
    return null;
  }

  const heading = [
    recap.visitNumber !== null
      ? t('carePlans.visitNumber', { number: recap.visitNumber })
      : null,
    recap.phase
      ? `${t('carePlans.phaseNumber', { number: recap.phase.current })}${
          recap.phase.name
            ? ` · ${recap.phase.name.replace(/^Phase\s+\d+\s*[—–-]\s*/i, '')}`
            : ''
        }`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <section className="w-full rounded-lg border border-border-secondary bg-bg-primary px-4 py-3.5">
      <Stack direction="vertical" gap={0}>
        <Text size="md" weight="semibold">
          {t('carePlans.recap.title')}
        </Text>
        {heading && (
          <Text size="xs" weight="medium" color="tertiary">
            {heading}
          </Text>
        )}
      </Stack>

      <Grid columns={1} className="mt-2 sm:grid-cols-3">
        <RecapColumn title={t('carePlans.recap.goals')} className="sm:pr-4">
          {recap.goals.length === 0 ? (
            <Empty>{t('carePlans.recap.noGoals')}</Empty>
          ) : (
            recap.goals.map((g) => (
              <Stack key={g.id} direction="vertical" gap={0}>
                <Text size="xs">{g.text}</Text>
                {g.targetDate && (
                  <Text size="xs" color="tertiary">
                    {t('carePlans.recap.targetDate', {
                      date: compactDate(g.targetDate),
                    })}
                  </Text>
                )}
              </Stack>
            ))
          )}
        </RecapColumn>

        <RecapColumn
          title={t('carePlans.recap.doneLastVisit')}
          className="sm:border-l sm:px-4"
        >
          {recap.lastVisitActionItems.length === 0 ? (
            <Empty>{t('carePlans.recap.noExercises')}</Empty>
          ) : (
            <>
              {recap.lastVisitActionItems.map((a) => (
                <Stack key={a.id} direction="horizontal" align="start" gap={2}>
                  <Icon
                    name="Check"
                    size="xs"
                    className="mt-0.5 shrink-0 text-text-tertiary"
                  />
                  <Text size="xs">{a.text}</Text>
                </Stack>
              ))}
              <Text size="xs" color="tertiary">
                {t('carePlans.recap.completedCount', {
                  count: recap.lastVisitActionItems.length,
                })}
              </Text>
            </>
          )}
        </RecapColumn>

        <RecapColumn
          title={t('carePlans.recap.addressThisVisit')}
          className="sm:border-l sm:pl-4"
        >
          {recap.measuresDue.length === 0 ? (
            <Empty>{t('carePlans.recap.nothingDue')}</Empty>
          ) : (
            recap.measuresDue.map((m) => (
              <Text key={m.key} size="xs">
                {m.label}{' '}
                {m.lastValue !== null && (
                  <span className="text-text-tertiary">
                    · {t('carePlans.recap.lastValue', { value: m.lastValue })}
                    {m.unit ? ` ${m.unit}` : ''}
                    {m.lastDate ? ` (${compactDate(m.lastDate)})` : ''}
                  </span>
                )}
              </Text>
            ))
          )}
        </RecapColumn>
      </Grid>
    </section>
  );
};

const RecapColumn: FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, children, className }) => (
  <Stack
    direction="vertical"
    gap={2}
    className={`border-border-secondary py-4 ${className ?? ''}`}
  >
    <Text
      size="xs"
      weight="bold"
      color="tertiary"
      className="tracking-wide uppercase"
    >
      {title}
    </Text>
    {children}
  </Stack>
);

const Empty: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text size="xs" color="tertiary">
    {children}
  </Text>
);
